import "./Estimation1.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaFileInvoiceDollar,
  FaEye,
  FaEdit,
  FaTrash,
  FaWallet,
  FaChartPie,
  FaPlus,
  FaTimes,
  FaFilePdf,
} from "react-icons/fa";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { db } from "../../firebase/firebase";
import {
  collection,
  getDocs,
  doc,
  // getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import {
  useClientsStatusMap,
  resolveClientStatus,
  getProjectRef,
  PROJECT_REF_BADGE_STYLE,
} from "../../hooks/useClientsStatusMap";
import { useToast } from "./Invoicefiles/Usetoast";
import ToastContainer from "./Invoicefiles/Toastcontainer";
import usePagination from "../../hooks/usePagination";
import Pagination from "./Pagination";


// import ConfirmDialog from "./Invoicefiles/Confirmdialog";
// Same validation rules used on the Clients page, kept in sync so an edit
// made from either page enforces the same format.
const EMAIL_REGEX =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
const PHONE_REGEX = /^[6-9]\d{9}$/;

const PROJECT_TYPE_OPTIONS = ["Residential", "Commercial", "Renovation", "Interiors"];
// BUGFIX: "Completed" is now a normal, always-selectable status here too —
// it previously had no entry in STATUS_OPTIONS and the select was disabled
// once a client reached "Completed", which made it behave differently from
// "In Talk" / "In Progress". It now saves through the exact same
// updateDoc(doc(db, "clients", ...)) call as every other status.
const STATUS_OPTIONS = ["In Talk", "In Progress", "Completed"];

// Once a client's estimation has been moved to Invoice, money has already
// changed hands and work is expected to start — they can no longer be
// "In Talk". The edit modal's status dropdown is restricted to these two
// once `estimation.invoiced` is true, so "In Talk" simply isn't offered
// as an option anymore for that client.
const POST_INVOICE_STATUS_OPTIONS = ["In Progress", "Completed"];

// ---------------- Budget Planning defaults ----------------
// NOTE: Budget Planning no longer starts with any preset/default
// categories (Sand, Cement, Steel, etc). The popup now always opens with
// an empty row list when a client has no plan yet, and the administrator
// builds the budget manually using "+ Add Item". Every row is still fully
// editable (name, price/qty, quantity, notes) and existing plans continue
// to load and display exactly as saved.
//
// ---------------- Estimation amount is now DERIVED from Budget Planning ----------------
// A client moved to Estimation (see Clients.js's moveToEstimation) no
// longer arrives with a pre-set amount — the Estimation/Quotation total is
// always the sum of every Budget Planning line item (see saveBudgetPlan
// below). The old "planned budget cannot exceed the quotation" cap has
// been removed, since the quotation itself is now computed FROM the
// planned budget instead of the other way around. Every Add/Edit/Delete
// of a budget item followed by "Save Budget Plan" recalculates the total
// and pushes it onto the SAME existing estimation document (never a
// second/competing record), so it stays persistent across refreshes and
// is what Materials.js / Invoice.js already read as the quotation amount.

// =====================================================================
// Budget Allocation Report (PDF) — read-only export from the View Budget
// Allocation modal. Everything below is presentation-only: it builds the
// PDF straight from `viewBudgetData` (the same budget-plan doc already
// shown on screen) plus the client fields attached to it when the modal
// opens. No Firestore reads/writes happen here, and none of the budget
// allocation logic, calculations, or Move-to-Invoice flow is touched.
// =====================================================================

const COMPANY_NAME = "Sarrathi Constructions";
const COMPANY_ADDRESS =
  "82, Jagannathan Salai, Anjugam Nagar,SRP Colony, Kolathur, Chennai - 600082.";
const LOGO_PATH = "/logo/company logo.jpeg";
const COMPANY_EMAIL = "build@sarrathiconstructions.com";
const COMPANY_PHONE = "98405 24873, 98407 98059";
const COMPANY_WEBSITE = "www.sarrathiconstructions.com";

// Shared visual language, matching the redesigned Invoice.js and
// Materials.js report PDFs so a client sees consistent branding across
// every document this app generates.
const PDF_THEME = {
  colors: {
    primaryDark: [23, 32, 51],
    primary: [22, 37, 61],
    accentGold: [199, 156, 68],
    textMuted: [110, 118, 132],
    textBody: [55, 65, 81],
    borderLight: [225, 228, 233],
    rowStripe: [246, 247, 250],
    balanceRed: [196, 78, 66],
    paidGreen: [46, 138, 89],
  },
  fontFamily: "helvetica",
};

// Loads the company logo as a base64 data URL so jsPDF can embed it.
// Read-only, local static asset fetch — no Firestore access involved.
const loadCompanyLogoDataUrl = async () => {
  try {
    const res = await fetch(LOGO_PATH);
    if (!res.ok) throw new Error("Logo not found");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("Failed to load company logo for PDF:", err);
    return null;
  }
};

// Turns a client name into a safe, non-generic PDF filename.
const sanitizeFileNameForPdf = (name) => {
  const cleaned = (name || "").trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return cleaned || "Client";
};

// PDF-only currency formatter. jsPDF's built-in "helvetica" font only
// supports the WinAnsi character set, which has no glyph for "₹" (Indian
// Rupee, U+20B9) — passing that character to docPdf.text()/autoTable
// renders a stray fallback glyph and can corrupt the rest of the string
// on that line. The on-screen `formatRupees` inside the component (which
// still shows "₹") is untouched; only this PDF export uses the ASCII-safe
// "Rs." prefix, which every standard PDF font renders correctly.
const formatRupeesPdf = (value) => {
  const num = Number(value);
  if (!value || Number.isNaN(num)) return "Rs. 0";
  return `Rs. ${num.toLocaleString("en-IN")}`;
};

const drawSectionTitle = (docPdf, text, x, y) => {
  const { colors } = PDF_THEME;
  docPdf.setFont(PDF_THEME.fontFamily, "bold");
  docPdf.setFontSize(11.5);
  docPdf.setTextColor(...colors.primaryDark);
  docPdf.text(text.toUpperCase(), x, y);
  docPdf.setDrawColor(...colors.accentGold);
  docPdf.setLineWidth(1.1);
  docPdf.line(x, y + 5, x + 34, y + 5);
  return y + 22;
};

const baseTableOptions = () => ({
  theme: "grid",
  styles: {
    font: PDF_THEME.fontFamily,
    fontSize: 9,
    cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
    textColor: PDF_THEME.colors.textBody,
    lineColor: PDF_THEME.colors.borderLight,
    lineWidth: 0.6,
    valign: "middle",
  },
  headStyles: {
    fillColor: PDF_THEME.colors.primary,
    textColor: 255,
    fontStyle: "bold",
    fontSize: 9,
    halign: "left",
  },
  alternateRowStyles: { fillColor: PDF_THEME.colors.rowStripe },
});

// Height reserved at the top of every page for a repeating header — the
// full company header only appears on page 1; pages 2+ get a slim
// repeated header (drawn in the final pass) that lives inside this same
// band, so table content set with margin.top = PDF_HEADER_BAND never
// starts underneath it on a page break, whether manually triggered or one
// autoTable adds internally for a long items table.
const PDF_HEADER_BAND = 74;

function Estimation1() {
  const [estimations, setEstimations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Single source of truth for status + client details (name, phone, etc),
  // shared with the Clients, Invoice, and Materials pages via a realtime
  // Firestore subscription — see src/hooks/useClientsStatusMap.js.
  const { clientsById } = useClientsStatusMap();

  const { toasts, toast } = useToast();

// Replaces window.confirm() in deleteEstimation
const [pendingDeleteEstimation, setPendingDeleteEstimation] = useState(null);
const [deletingEstimation, setDeletingEstimation] = useState(false);
const [movingEstToRejected, setMovingEstToRejected] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingEstimation, setViewingEstimation] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEstimation, setEditingEstimation] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    projectType: "",
    status: "In Talk",
    estimationAmount: "",
  });
  const [editErrors, setEditErrors] = useState({ email: "", phone: "" });
  const [saving, setSaving] = useState(false);

  // ---------------- Move to Invoice ----------------
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingEstimation, setMovingEstimation] = useState(null);
  const [finalQuotation, setFinalQuotation] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [moveError, setMoveError] = useState("");
  const [movingSaving, setMovingSaving] = useState(false);

  // Holds { final, paid } between "Confirm & Move to Invoice" and the
  // actual invoice write, while we resolve the budget-rebalance question.
  const [pendingInvoiceData, setPendingInvoiceData] = useState(null);
  const [showRebalanceChoice, setShowRebalanceChoice] = useState(false);

  // ---------------- Budget Planning (Materials column) ----------------
  // clientDocId -> { clientDocId, estimationId, quotationAmount, items, totalPlanned }
  // Each item is stored as { name, pricePerQty, quantity, budget, notes }.
  // `budget` is always the computed total (pricePerQty * quantity) and is
  // kept as the field name for backward compatibility with the existing
  // auto-rebalance logic below, which scales it proportionally.
  const [budgetPlansByClient, setBudgetPlansByClient] = useState({});

  // Safety-net map: clientDocId -> finalEstimation, read once from the
  // `invoices` collection. Under normal operation `estimationAmount` on an
  // already-invoiced estimation is kept in sync with the invoice's Final
  // Quotation (see finalizeMoveToInvoice below, and Payments.js's
  // saveQuotation). This map exists purely so that ANY already-invoiced
  // project — including ones invoiced before that sync existed — never
  // displays a stale pre-invoice estimation amount here. Read-only.
  const [finalQuotationByClient, setFinalQuotationByClient] = useState({});

  const fetchFinalQuotationMap = async () => {
    try {
      const snapshot = await getDocs(collection(db, "invoices"));
      const map = {};
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.clientDocId) {
          map[data.clientDocId] = Number(data.finalEstimation) || 0;
        }
      });
      setFinalQuotationByClient(map);
    } catch (err) {
      console.error("Failed to load invoice quotations:", err);
    }
  };

  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetEstimation, setBudgetEstimation] = useState(null);
  const [budgetRows, setBudgetRows] = useState([]);
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [budgetError, setBudgetError] = useState("");
  // True when the Budget Planning popup was opened as part of the
  // "manually edit" branch of Move To Invoice — saving it should then
  // resume and finish creating the invoice.
  const [pendingMoveAfterBudget, setPendingMoveAfterBudget] = useState(false);
  // Used to hand out stable, unique ids to custom rows added via "+ Add Item".
  const budgetRowIdCounter = useRef(0);

  // Auto-scroll-to-new-row support: tracks the id of the most recently
  // added row so the effect below can scroll to it and focus its Item
  // Name input once it's rendered.
  const [newlyAddedRowId, setNewlyAddedRowId] = useState(null);
  const budgetRowsScrollRef = useRef(null);
  const newRowNameInputRef = useRef(null);

  const [showViewBudgetModal, setShowViewBudgetModal] = useState(false);
  const [viewBudgetData, setViewBudgetData] = useState(null);
  const [budgetPdfGenerating, setBudgetPdfGenerating] = useState(false);

  const fetchEstimations = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "estimations"));
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      // Newest first, when a timestamp is available. (Estimation page only.)
      list.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bTime - aTime;
      });

      setEstimations(list);
    } catch (err) {
      console.error("Failed to load estimations:", err);
    } finally {
      setLoading(false);
    }
  };

  // Budget plans live in their own collection, one doc per client, keyed
  // by clientDocId (reusing the existing clientDocId architecture instead
  // of duplicating any client/estimation info).
  const fetchBudgetPlans = async () => {
    try {
      const snapshot = await getDocs(collection(db, "budgetPlans"));
      const map = {};
      snapshot.forEach((docSnap) => {
        map[docSnap.id] = docSnap.data();
      });
      setBudgetPlansByClient(map);
    } catch (err) {
      console.error("Failed to load budget plans:", err);
    }
  };

  useEffect(() => {
    fetchEstimations();
    fetchBudgetPlans();
    fetchFinalQuotationMap();
  }, []);

  const formatRupees = (value) => {
    const num = Number(value);
    if (!value || Number.isNaN(num)) return "-";
    return `₹ ${num.toLocaleString("en-IN")}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return dateObj.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  // The single active quotation amount to DISPLAY for an estimation row.
  // Once a project has been moved to Invoice, the Final Quotation is the
  // active amount everywhere in the app — this never falls back to the
  // old pre-invoice estimation, even for projects invoiced before
  // estimationAmount was kept in sync with the invoice.
  const displayedEstimationAmount = (estimation) => {
    if (estimation?.invoiced && finalQuotationByClient[estimation.clientDocId] !== undefined) {
      return finalQuotationByClient[estimation.clientDocId];
    }
    return estimation?.estimationAmount;
  };

  // Status to display for a given estimation row — always resolved from
  // the live clients map, never from a locally-stored copy.
  const statusFor = (estimation) =>
    resolveClientStatus(clientsById, estimation.clientDocId, "In Talk");

  // Which statuses are selectable for a given estimation's client. Once
  // the estimation has been moved to Invoice, "In Talk" is no longer a
  // valid state for that client — they've already paid and work should
  // be underway, so only "In Progress" / "Completed" remain.
  const statusOptionsFor = (estimation) =>
    estimation?.invoiced ? POST_INVOICE_STATUS_OPTIONS : STATUS_OPTIONS;

  const filteredEstimations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return estimations;

    return estimations.filter((e) => {
      const clientId = (e.clientId || "").toLowerCase();
      const name = (e.name || "").toLowerCase();
      const email = (e.email || "").toLowerCase();

      return (
        clientId.includes(term) || name.includes(term) || email.includes(term)
      );
    });
  }, [search, estimations]);

  const {
    pageData: paginatedEstimations,
    ...estimationsPagination
  } = usePagination(filteredEstimations, { resetKey: search });

  const totalValue = useMemo(
    () => estimations.reduce((sum, e) => sum + (Number(displayedEstimationAmount(e)) || 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [estimations, finalQuotationByClient],
  );

  // ---------------- View ----------------
  const openView = (estimation) => {
    setViewingEstimation({
      ...estimation,
      phone: clientsById[estimation.clientDocId]?.phone || "",
      status: statusFor(estimation),
      createdAt: estimation.createdAt || clientsById[estimation.clientDocId]?.createdAt,
      estimationAmount: displayedEstimationAmount(estimation),
    });
    setShowViewModal(true);
  };

  const closeView = () => {
    setShowViewModal(false);
    setViewingEstimation(null);
  };

  // ---------------- Edit ----------------
  const openEdit = (estimation) => {
    const linkedClient = clientsById[estimation.clientDocId] || {};
    const resolvedStatus = statusFor(estimation);
    const allowedStatuses = statusOptionsFor(estimation);

    // Defensive fallback: an invoiced client should never actually be
    // sitting at "In Talk", but if older data somehow left it that way,
    // don't open the form on a status that isn't in its own options list.
    const safeStatus = allowedStatuses.includes(resolvedStatus)
      ? resolvedStatus
      : allowedStatuses[0];

    setEditForm({
      name: estimation.name || "",
      phone: linkedClient.phone || "",
      email: estimation.email || "",
      address: estimation.address || "",
      projectType: estimation.projectType || "",
      status: safeStatus,
      estimationAmount: String(displayedEstimationAmount(estimation) ?? ""),
    });
    setEditErrors({ email: "", phone: "" });
    setEditingEstimation(estimation);
    setShowEditModal(true);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditingEstimation(null);
    setEditErrors({ email: "", phone: "" });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setEditForm((prev) => ({ ...prev, phone: digitsOnly }));
      return;
    }

    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  // Estimation Amount is no longer part of this form's validation — it is
  // always derived from Budget Planning (see saveBudgetPlan below), so
  // this only validates the plain client-detail fields.
  const validateEditForm = () => {
    const errors = { email: "", phone: "" };
    let isValid = true;

    if (!EMAIL_REGEX.test(editForm.email.trim())) {
      errors.email = "Enter a valid email address (e.g. name@example.com).";
      isValid = false;
    }

    if (!PHONE_REGEX.test(editForm.phone.trim())) {
      errors.phone = "Enter a valid 10-digit Indian mobile number.";
      isValid = false;
    }

    setEditErrors(errors);
    return isValid;
  };

  const saveEdit = async () => {
    if (!editingEstimation) return;

    if (!validateEditForm()) {
      return;
    }

    setSaving(true);

    try {
      // Update the source-of-truth client record — status included, so
      // Clients/Invoice/Materials all see the change immediately.
      if (editingEstimation.clientDocId) {
        await updateDoc(doc(db, "clients", editingEstimation.clientDocId), {
          name: editForm.name,
          phone: editForm.phone,
          email: editForm.email,
          address: editForm.address,
          projectType: editForm.projectType,
          
        });
      }

      // Update the existing estimation document — never create a new one.
      // Status is intentionally NOT stored here; it always comes from the
      // client record. estimationAmount is also intentionally excluded —
      // it is only ever changed by Budget Planning now (see
      // saveBudgetPlan), so this can never overwrite it with a stale
      // manually-typed number.
      await updateDoc(doc(db, "estimations", editingEstimation.id), {
        name: editForm.name,
        email: editForm.email,
        address: editForm.address,
        projectType: editForm.projectType,
      });

      toast.success("Estimation Updated Successfully");

      closeEdit();
      await fetchEstimations();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update estimation.");
    } finally {
      setSaving(false);
    }
  };

  // ---------------- Delete ----------------
  // BUSINESS RULE: a client can only be rejected/deleted while still in
  // the Estimation stage. `estimation.invoiced` is the existing flag
  // already used by isMovable()/statusOptionsFor() above to mean "this
  // project has already entered Invoice" — reused here as-is rather than
  // introducing any new field.
 const deleteEstimation = (estimation) => {
  if (estimation.invoiced) {
    toast.error(
      "This project has already been moved to Invoice and can no longer be deleted or moved to Rejected.",
    );
    return;
  }
  setPendingDeleteEstimation(estimation);
};

// NEW — mirrors Clients.js's confirmMoveToRejected: a plain updateDoc on
// the client's status field, the single source of truth every other page
// (useClientsStatusMap) already reads from. No new document, no new
// collection, no duplicated status field. All estimation data, client
// data, and relationships (clientDocId etc.) are left completely
// untouched — only the client's status changes.
const confirmMoveEstimationToRejected = async () => {
  if (!pendingDeleteEstimation) return;

  // Defensive re-check — the invoiced flag could theoretically change
  // between opening the modal and confirming (e.g. another tab moved it
  // to Invoice in the meantime). Never allow a bypass.
  if (pendingDeleteEstimation.invoiced) {
    toast.error(
      "This project has already been moved to Invoice and can no longer be deleted or moved to Rejected.",
    );
    setPendingDeleteEstimation(null);
    return;
  }

  setMovingEstToRejected(true);
  try {
    await updateDoc(doc(db, "clients", pendingDeleteEstimation.clientDocId), {
      status: "Rejected",
    });
    toast.success("Client Moved to Rejected");
  } catch (err) {
    console.error(err);
    toast.error("Failed to move client to Rejected.");
  } finally {
    setMovingEstToRejected(false);
    setPendingDeleteEstimation(null);
  }
};

const confirmDeleteEstimation = async () => {
  if (!pendingDeleteEstimation) return;

  // Defensive re-check, same reasoning as confirmMoveEstimationToRejected.
  if (pendingDeleteEstimation.invoiced) {
    toast.error(
      "This project has already been moved to Invoice and can no longer be deleted or moved to Rejected.",
    );
    setPendingDeleteEstimation(null);
    return;
  }

  setDeletingEstimation(true);
  try {
    await deleteDoc(doc(db, "estimations", pendingDeleteEstimation.id));
    toast.success("Estimation Deleted Successfully");
    fetchEstimations();
  } catch (err) {
    console.error(err);
    toast.error("Failed to delete estimation.");
  } finally {
    setDeletingEstimation(false);
    setPendingDeleteEstimation(null);
  }
};
  // ================= Budget Planning =================

  const openBudgetPlanning = (estimation) => {
    const existing = budgetPlansByClient[estimation.clientDocId];

    if (existing?.items?.length) {
      setBudgetRows(
        existing.items.map((item, idx) => ({
          id: `row-${idx}`,
          name: item.name,
          // Legacy plans only stored a flat `budget` total. Reconstruct a
          // sensible price/qty split for them so old data still displays
          // correctly in the new per-unit layout.
          pricePerQty: String(item.pricePerQty ?? item.budget ?? ""),
          quantity: String(item.quantity ?? 1),
          notes: item.notes || "",
        })),
      );
    } else {
      // No existing plan for this client — start completely empty. The
      // administrator adds only the items they actually need via
      // "+ Add Item"; no preset/default categories are shown anymore.
      setBudgetRows([]);
    }

    setBudgetEstimation(estimation);
    setBudgetError("");
    setShowBudgetModal(true);
  };

  const closeBudgetModal = () => {
    if (budgetSaving) return;
    setShowBudgetModal(false);
    setBudgetEstimation(null);
    setBudgetRows([]);
    setBudgetError("");
    setPendingMoveAfterBudget(false);
  };

  const handleBudgetRowChange = (id, field, value) => {
    if (
      (field === "pricePerQty" || field === "quantity") &&
      value !== "" &&
      !/^\d*\.?\d*$/.test(value)
    ) {
      return;
    }
    setBudgetRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
    if (budgetError) setBudgetError("");
  };

  // Total is always derived — users can never type into it directly.
  const rowTotal = (row) => (Number(row.pricePerQty) || 0) * (Number(row.quantity) || 0);

  const addBudgetRow = () => {
    budgetRowIdCounter.current += 1;
    const newRowId = `row-custom-${Date.now()}-${budgetRowIdCounter.current}`;
    setBudgetRows((prev) => [
      ...prev,
      {
        id: newRowId,
        name: "",
        pricePerQty: "",
        quantity: "",
        notes: "",
      },
    ]);
    setNewlyAddedRowId(newRowId);
    if (budgetError) setBudgetError("");
  };

  const deleteBudgetRow = (id) => {
    setBudgetRows((prev) => prev.filter((row) => row.id !== id));
    if (budgetError) setBudgetError("");
  };

  // Whenever a new row is added, scroll it into view within the rows
  // container and focus its Item Name input so the user can start typing
  // immediately.
  useEffect(() => {
    if (!newlyAddedRowId) return;

    const frame = requestAnimationFrame(() => {
      newRowNameInputRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      newRowNameInputRef.current?.focus();
      setNewlyAddedRowId(null);
    });

    return () => cancelAnimationFrame(frame);
  }, [newlyAddedRowId, budgetRows]);

const budgetTotalPlanned = useMemo(
    () => budgetRows.reduce((sum, row) => sum + rowTotal(row), 0),
    [budgetRows],
  );

  // ---------------------------------------------------------------------
  // Save Budget Plan — this is now the ONLY place the Estimation/Quotation
  // amount is ever set. The total of every line item (Material, Labour,
  // Sand, Bricks, Cement, Steel, Electrical, Plumbing, Interiors, or any
  // other category the admin adds) becomes the new quotation, replacing
  // whatever amount existed before — including the ₹0 a client starts
  // with right after "Move to Estimation". There is no more cap check
  // against a pre-set quotation, because the quotation is now computed
  // FROM this total rather than constraining it.
  // ---------------------------------------------------------------------
  const saveBudgetPlan = async () => {
    if (!budgetEstimation?.clientDocId) return;

    setBudgetSaving(true);

    try {
      const items = budgetRows
        .filter((row) => row.name.trim())
        .map((row) => ({
          name: row.name.trim(),
          pricePerQty: Number(row.pricePerQty) || 0,
          quantity: Number(row.quantity) || 0,
          // `budget` stays the computed total, kept for backward
          // compatibility with the auto-rebalance logic and any other
          // reader of this collection.
          budget: rowTotal(row),
          notes: row.notes || "",
        }));

      // The new Estimation / Quotation Total — sum of every valid item's
      // amount, regardless of category. If there are no items yet, this
      // is 0, which is exactly the "no budget items" edge case the app
      // should show as ₹0 rather than blank/undefined.
      const newQuotationTotal = items.reduce((sum, item) => sum + (Number(item.budget) || 0), 0);

      const existingPlan = budgetPlansByClient[budgetEstimation.clientDocId];

      const planData = {
        clientDocId: budgetEstimation.clientDocId,
        estimationId: budgetEstimation.id,
        quotationAmount: newQuotationTotal,
        items,
        totalPlanned: newQuotationTotal,
        createdAt: existingPlan?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      // Doc id === clientDocId, so the plan is always linked to exactly
      // one client without duplicating any client information — this
      // UPDATES the same existing document, never a second one.
      await setDoc(doc(db, "budgetPlans", budgetEstimation.clientDocId), planData);

      // Push the newly calculated total onto the SAME existing estimation
      // document (never a new/competing record) so it is what
      // Materials.js and Invoice.js already read as the quotation amount,
      // and so it survives a page refresh.
      await updateDoc(doc(db, "estimations", budgetEstimation.id), {
        estimationAmount: newQuotationTotal,
      });

      // ---------------------------------------------------------------
      // BUGFIX — keep the linked Invoice in sync with the Estimation
      // total, even after the client has already been moved to Invoice.
      // Estimation's BOQ total is always the source of truth: whenever
      // it changes, the invoice's finalEstimation must be updated to
      // match, and balanceAmount recalculated from the EXISTING
      // amountPaid. Payment history (`payments`, `amountPaid`) is never
      // touched — only finalEstimation/balanceAmount are recalculated.
      // ---------------------------------------------------------------
      if (budgetEstimation.invoiced && budgetEstimation.clientDocId) {
        try {
          const linkedInvoiceQuery = query(
            collection(db, "invoices"),
            where("clientDocId", "==", budgetEstimation.clientDocId),
          );
          const linkedInvoiceSnap = await getDocs(linkedInvoiceQuery);

          if (!linkedInvoiceSnap.empty) {
            const invoiceDocSnap = linkedInvoiceSnap.docs[0];
            const invoiceData = invoiceDocSnap.data();
            const amountPaid = Number(invoiceData.amountPaid) || 0;
            const newBalance = newQuotationTotal - amountPaid;

            await updateDoc(doc(db, "invoices", invoiceDocSnap.id), {
              finalEstimation: newQuotationTotal,
              balanceAmount: newBalance,
            });
          }
        } catch (syncErr) {
          console.error("Failed to sync estimation total to invoice:", syncErr);
        }
      }

      setBudgetPlansByClient((prev) => ({
        ...prev,
        [budgetEstimation.clientDocId]: { ...planData, updatedAt: undefined },
      }));

      // Reflect the new total immediately in the table and in the modal's
      // own local state, without waiting on a full refetch.
      setEstimations((prev) =>
        prev.map((est) =>
          est.id === budgetEstimation.id
            ? { ...est, estimationAmount: newQuotationTotal }
            : est,
        ),
      );
      setBudgetEstimation((prev) =>
        prev ? { ...prev, estimationAmount: newQuotationTotal } : prev,
      );

      if (pendingMoveAfterBudget && pendingInvoiceData) {
        // This save happened mid Move-To-Invoice (manual rebalance path) —
        // resume and finish creating the invoice now that the plan is valid.
        setPendingMoveAfterBudget(false);
        setShowBudgetModal(false);
        setBudgetEstimation(null);
        setBudgetRows([]);
        setBudgetError("");
        await finalizeMoveToInvoice(pendingInvoiceData.final, pendingInvoiceData.paid);
      } else {
        toast.success("Budget Plan Saved — Estimation Total Updated");
        closeBudgetModal();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save budget plan.");
    } finally {
      setBudgetSaving(false);
    }
  };

  // ---------------- View Budget (read-only) ----------------
  const openViewBudget = (estimation) => {
    const plan = budgetPlansByClient[estimation.clientDocId];
    if (!plan) {
      toast.warning("No budget plan has been created for this client yet.");
      return;
    }
    // clientName / clientId / projectType are attached here purely for the
    // Generate PDF export below — the existing View Budget modal never
    // reads or renders these fields, so this changes nothing about what's
    // currently displayed on screen.
    setViewBudgetData({
      ...plan,
      clientName: estimation.name || "",
      clientId: estimation.clientId || "",
      projectType: estimation.projectType || "",
    });
    setShowViewBudgetModal(true);
  };

  const closeViewBudget = () => {
    setShowViewBudgetModal(false);
    setViewBudgetData(null);
  };

  // =====================================================================
  // Generate PDF — Budget Allocation Report.
  //
  // Strictly read-only: builds the PDF from `viewBudgetData`, exactly the
  // same object already driving the View Budget modal on screen. No
  // Firestore reads/writes happen here, and none of the budget planning,
  // rebalancing, or Move-to-Invoice logic above is touched.
  // =====================================================================
  const generateBudgetPDF = async () => {
    if (!viewBudgetData) return;

    setBudgetPdfGenerating(true);
    try {
      const { colors } = PDF_THEME;
      const docPdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = docPdf.internal.pageSize.getWidth();
      const marginX = 40;

      // ---- Full header (page 1) — logo, company name, address ----
      const logoDataUrl = await loadCompanyLogoDataUrl();
      const headerTop = 40;
      const logoSize = 54;

      if (logoDataUrl) {
        try {
          docPdf.addImage(logoDataUrl, "JPEG", marginX, headerTop, logoSize, logoSize);
        } catch (imgErr) {
          console.error("Failed to draw logo on budget report PDF:", imgErr);
        }
      }

      const textX = marginX + logoSize + 16;
      const nameY = headerTop + logoSize / 2 - 6;

      docPdf.setFont(PDF_THEME.fontFamily, "bold");
      docPdf.setFontSize(17);
      docPdf.setTextColor(...colors.primaryDark);
      docPdf.text(COMPANY_NAME, textX, nameY);

      docPdf.setFont(PDF_THEME.fontFamily, "normal");
      docPdf.setFontSize(9);
      docPdf.setTextColor(...colors.textMuted);
            docPdf.text(COMPANY_ADDRESS, textX, nameY + 15, {
        maxWidth: pageWidth - marginX * 2 - logoSize - 16,
      });
      docPdf.text(`Phone: ${COMPANY_PHONE}`, textX, nameY + 27);
      docPdf.text(`Email: ${COMPANY_EMAIL}`, textX, nameY + 39);
      docPdf.text(`Website: ${COMPANY_WEBSITE}`, textX, nameY + 51);

      // Divider under the logo/company row — the report title lives on
      // its own row BELOW this line, never sharing a row with the company
      // name, so a long title can never collide with it.
            const logoRowBottom = Math.max(headerTop + logoSize, nameY + 51) + 16;
      docPdf.setDrawColor(...colors.accentGold);
      docPdf.setLineWidth(1.3);
      docPdf.line(marginX, logoRowBottom, pageWidth - marginX, logoRowBottom);

      const titleY = logoRowBottom + 26;

      docPdf.setFont(PDF_THEME.fontFamily, "bold");
      docPdf.setFontSize(15);
      docPdf.setTextColor(...colors.primaryDark);
      docPdf.text("BUDGET ALLOCATION REPORT", marginX, titleY);

      docPdf.setFont(PDF_THEME.fontFamily, "normal");
      docPdf.setFontSize(9.5);
      docPdf.setTextColor(...colors.textMuted);
      docPdf.text(
        `Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`,
        pageWidth - marginX,
        titleY,
        { align: "right" },
      );

      const headerBottom = titleY + 16;
      docPdf.setDrawColor(...colors.borderLight);
      docPdf.setLineWidth(0.8);
      docPdf.line(marginX, headerBottom, pageWidth - marginX, headerBottom);

      let cursorY = headerBottom + 22;

      // ---- Client & Project Information ----
      cursorY = drawSectionTitle(docPdf, "Client & Project Information", marginX, cursorY);

      autoTable(docPdf, {
        ...baseTableOptions(),
        startY: cursorY,
        showHead: false,
        styles: { ...baseTableOptions().styles, fontSize: 9.5 },
        body: [
          ["Client Name", viewBudgetData.clientName || "-", "Client ID", viewBudgetData.clientId || "-"],
          [
            "Project Type",
            viewBudgetData.projectType || "-",
            "Estimation Amount",
            formatRupeesPdf(viewBudgetData.quotationAmount),
          ],
        ],
        columnStyles: {
          0: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 100 },
          1: { cellWidth: 185 },
          2: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 100 },
          3: { cellWidth: "auto" },
        },
        margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
      });

      cursorY = docPdf.lastAutoTable.finalY + 26;

      // ---- Budget Allocation table ----
      cursorY = drawSectionTitle(docPdf, "Budget Allocation", marginX, cursorY);

      const items = viewBudgetData.items || [];
      const rows = items.map((item) => {
        const qty = item.quantity ?? 1;
        const pricePerQty =
          item.pricePerQty ?? (qty ? (Number(item.budget) || 0) / qty : Number(item.budget) || 0);
        return [
          item.name || "-",
          { content: formatRupeesPdf(pricePerQty), styles: { halign: "right" } },
          String(qty),
          { content: formatRupeesPdf(item.budget), styles: { halign: "right", fontStyle: "bold" } },
          item.notes || "-",
        ];
      });

      if (rows.length === 0) {
        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(9.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("No items in this budget plan.", marginX, cursorY + 4);
        cursorY += 26;
      } else {
        autoTable(docPdf, {
          ...baseTableOptions(),
          startY: cursorY,
          head: [["Items", "Price", "Quantity", "Total", "Notes"]],
          body: rows,
          columnStyles: {
            0: { cellWidth: 145 },
            1: { cellWidth: 85, halign: "right" },
            2: { cellWidth: 70, halign: "center" },
            3: { cellWidth: 90, halign: "right" },
            4: { cellWidth: "auto" },
          },
          margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
        });
        cursorY = docPdf.lastAutoTable.finalY + 26;
      }

      // ---- Totals ----
      if (cursorY > 690) {
        docPdf.addPage();
        cursorY = 96;
      }

      docPdf.setDrawColor(...colors.borderLight);
      docPdf.setLineWidth(0.8);
      docPdf.line(marginX, cursorY, pageWidth - marginX, cursorY);
      cursorY += 20;

      const remaining = (Number(viewBudgetData.quotationAmount) || 0) -
        (Number(viewBudgetData.totalPlanned) || 0);

      autoTable(docPdf, {
        startY: cursorY,
        theme: "plain",
        styles: {
          font: PDF_THEME.fontFamily,
          fontSize: 10.5,
          cellPadding: { top: 6, bottom: 6, left: 0, right: 0 },
          fontStyle: "bold",
          textColor: colors.primaryDark,
        },
        body: [
          ["Total Planned Budget", formatRupeesPdf(viewBudgetData.totalPlanned)],
          [
            "Remaining Budget",
            { content: formatRupeesPdf(remaining), styles: { textColor: remaining < 0 ? colors.balanceRed : colors.paidGreen } },
          ],
          ["Quotation Amount", formatRupeesPdf(viewBudgetData.quotationAmount)],
        ],
        columnStyles: { 1: { halign: "right" } },
        margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
      });

      // ---- Repeated slim header (pages 2+) and footer (every page) ----
      const pageCount = docPdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i += 1) {
        docPdf.setPage(i);
        const pageHeight = docPdf.internal.pageSize.getHeight();

        if (i > 1) {
          docPdf.setFont(PDF_THEME.fontFamily, "bold");
          docPdf.setFontSize(11);
          docPdf.setTextColor(...colors.primaryDark);
          docPdf.text(COMPANY_NAME, marginX, 30);

          docPdf.setFont(PDF_THEME.fontFamily, "normal");
          docPdf.setFontSize(9);
          docPdf.setTextColor(...colors.textMuted);
          docPdf.text(
            `Budget Allocation Report — ${viewBudgetData.clientName || "Client"}`,
            pageWidth - marginX,
            30,
            { align: "right" },
          );

          docPdf.setDrawColor(...colors.accentGold);
          docPdf.setLineWidth(1);
          docPdf.line(marginX, 44, pageWidth - marginX, 44);
        }

        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(8.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("Thank you for choosing Sarrathi Constructions.", marginX, pageHeight - 30);
        docPdf.text("This report is system generated.", marginX, pageHeight - 18);
        docPdf.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 18, {
          align: "right",
        });
      }

      docPdf.save(`${sanitizeFileNameForPdf(viewBudgetData.clientName)}_Budget_Allocation.pdf`);
    } catch (err) {
      console.error("Failed to generate budget allocation PDF:", err);
      toast.error("Failed to generate budget allocation PDF.");
    } finally {
      setBudgetPdfGenerating(false);
    }
  };

  // A budget plan is considered "complete" once it has at least one saved
  // line item with a non-zero planned total. Move To Invoice is gated on
  // this so materials are always allocated before money changes hands.
  const hasBudgetPlan = (estimation) => {
    const plan = budgetPlansByClient[estimation.clientDocId];
    return !!(
      plan &&
      Array.isArray(plan.items) &&
      plan.items.length > 0 &&
      Number(plan.totalPlanned) > 0
    );
  };

  // ---------------- Move to Invoice ----------------
  const isMovable = (estimation) =>
    !estimation.invoiced && statusFor(estimation) === "In Talk";

  const openMoveToInvoice = (estimation) => {
    if (!isMovable(estimation)) return;

    if (!hasBudgetPlan(estimation)) {
      toast.warning("Please complete Budget Allocation before moving this project to Invoice.");
      return;
    }

    setMovingEstimation(estimation);
    setFinalQuotation(String(estimation.estimationAmount ?? ""));
    setAmountPaid("");
    setMoveError("");
    setShowMoveModal(true);
  };

  const closeMoveToInvoice = () => {
    if (movingSaving) return;
    setShowMoveModal(false);
    setMovingEstimation(null);
    setFinalQuotation("");
    setAmountPaid("");
    setMoveError("");
    setPendingInvoiceData(null);
    setShowRebalanceChoice(false);
  };

  const handleFinalQuotationChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFinalQuotation(value);
      if (moveError) setMoveError("");
    }
  };

  const handleAmountPaidChange = (e) => {
    const value = e.target.value;
    // allow empty string or a plain positive decimal number while typing
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setAmountPaid(value);
      if (moveError) setMoveError("");
    }
  };

  const moveBalanceAmount = useMemo(() => {
    const final = Number(finalQuotation) || 0;
    const paid = Number(amountPaid) || 0;
    const balance = final - paid;
    return balance > 0 ? balance : 0;
  }, [finalQuotation, amountPaid]);

  // Validates the Final Quotation / Amount Paid fields, then either goes
  // straight to invoice creation (no budget plan on file) or asks whether
  // to auto-rebalance the existing plan against the negotiated amount.
 const handleMoveConfirmClick = () => {
    if (!movingEstimation) return;

    const final = Number(finalQuotation);
    const paid = amountPaid.trim() === "" ? 0 : Number(amountPaid);

    if (!finalQuotation.trim() || Number.isNaN(final) || final <= 0) {
      setMoveError("Please enter a valid final quotation amount.");
      return;
    }

    // Advance payment is optional — an empty field means the client is
    // proceeding with no advance (Amount Paid = ₹0, Balance = full
    // estimation). Only a genuinely invalid (non-numeric or negative)
    // entry is rejected.
    if (amountPaid.trim() !== "" && (Number.isNaN(paid) || paid < 0)) {
      setMoveError("Please enter a valid amount paid.");
      return;
    }

    if (paid > final) {
      setMoveError("Amount paid cannot exceed the final quotation.");
      return;
    }

    setPendingInvoiceData({ final, paid });
finalizeMoveToInvoice(final, paid);
  };

  // autoRebalance = true  -> scale every planned budget proportionally so
  //                          the total equals the new final quotation.
  //                          Quantity is kept as-is and price/qty is scaled
  //                          so price × qty still equals the new total.
  // autoRebalance = false -> reopen Budget Planning, scoped to the new
  //                          quotation, for manual editing before the
  //                          invoice is actually created.
  const handleRebalanceChoice = async (autoRebalance) => {
    if (!movingEstimation || !pendingInvoiceData) return;
    const { final, paid } = pendingInvoiceData;
    const existingPlan = budgetPlansByClient[movingEstimation.clientDocId];

    setShowRebalanceChoice(false);

    if (autoRebalance && existingPlan?.items?.length) {
      const oldTotal = Number(existingPlan.quotationAmount) || 0;
      const scaledItems = existingPlan.items.map((item) => {
        const oldBudget = Number(item.budget) || 0;
        const scaledBudget = oldTotal > 0 ? (oldBudget * final) / oldTotal : 0;
        const qty = Number(item.quantity) || 0;
        const scaledPricePerQty =
          qty > 0 ? scaledBudget / qty : Number(item.pricePerQty) || 0;

        return {
          ...item,
          pricePerQty: Math.round(scaledPricePerQty * 100) / 100,
          quantity: qty,
          budget: Math.round(scaledBudget),
        };
      });
      const scaledTotal = scaledItems.reduce(
        (sum, item) => sum + (Number(item.budget) || 0),
        0,
      );

      try {
        const planData = {
          clientDocId: movingEstimation.clientDocId,
          estimationId: movingEstimation.id,
          quotationAmount: final,
          items: scaledItems,
          totalPlanned: scaledTotal,
          createdAt: existingPlan?.createdAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        await setDoc(doc(db, "budgetPlans", movingEstimation.clientDocId), planData);

        setBudgetPlansByClient((prev) => ({
          ...prev,
          [movingEstimation.clientDocId]: { ...planData, updatedAt: undefined },
        }));
      } catch (err) {
        console.error("Failed to rebalance budget plan:", err);
        alert(
          "Failed to rebalance the planned budget automatically. You can adjust it later from Budget Planning.",
        );
      }

      await finalizeMoveToInvoice(final, paid);
    } else {
      // Manual edit path — reopen Budget Planning pre-filled with the
      // existing rows, but scoped to the NEW quotation, and remember to
      // resume the invoice creation once a valid plan is saved.
      setBudgetEstimation({ ...movingEstimation, estimationAmount: final });
      setBudgetRows(
        (existingPlan?.items || []).map((item, idx) => ({
          id: `row-${idx}`,
          name: item.name,
          pricePerQty: String(item.pricePerQty ?? item.budget ?? ""),
          quantity: String(item.quantity ?? 1),
          notes: item.notes || "",
        })),
      );
      setBudgetError("");
      setPendingMoveAfterBudget(true);
      setShowBudgetModal(true);
    }
  };

  const finalizeMoveToInvoice = async (final, paid) => {
    if (!movingEstimation) return;

    setMovingSaving(true);

    try {
      const balance = final - paid;
      const linkedClient = clientsById[movingEstimation.clientDocId] || {};

      // Seed the payment history. The Final Quotation is negotiated here,
      // but the actual money movement lives entirely in `payments` — if an
      // amount was already paid at move time, that IS this project's
      // Invoice #1 (see Invoice page's "Add Payment", which appends
      // Invoice #2, #3... the same way from here on). If nothing has been
      // paid yet, payments starts empty and the very first "Add Payment"
      // on the Invoice page becomes Invoice #1.
      const today = new Date().toISOString().slice(0, 10);
      const initialPayments =
        paid > 0
          ? [
              {
                invoiceNumber: 1,
                paymentAmount: paid,
                paymentDate: today,
                balanceAfterPayment: balance,
                createdAt: new Date().toISOString(),
              },
            ]
          : [];

      // 1. Create the invoice document. `finalEstimation` holds the
      //    negotiated Final Quotation — the estimation's own
      //    estimationAmount (the Original Estimation) is left untouched.
      await addDoc(collection(db, "invoices"), {
        estimationId: movingEstimation.id,
        clientDocId: movingEstimation.clientDocId || "",
        clientId: movingEstimation.clientId || "",
        clientName: movingEstimation.name || "",
        email: movingEstimation.email || linkedClient.email || "",
        address: movingEstimation.address || linkedClient.address || "",
        projectType: movingEstimation.projectType || "",
        originalEstimation: Number(movingEstimation.estimationAmount) || 0,
        finalEstimation: final,
        amountPaid: paid,
        balanceAmount: balance,
        payments: initialPayments,
        paymentCount: initialPayments.length,
        latestPaymentDate: initialPayments.length ? today : null,
        createdAt: serverTimestamp(),
      });

      // 2. Update the client's status: In Talk -> In Progress. This is the
      //    single source of truth — Clients/Estimation/Materials all
      //    reflect it immediately.
      if (movingEstimation.clientDocId) {
        await updateDoc(doc(db, "clients", movingEstimation.clientDocId), {
          status: "In Progress",
        });
      }

      // 3. Mark the estimation itself as invoiced so it can't be moved twice.
      //    This is also what now permanently removes "In Talk" as an option
      //    on this client's Edit modal — see statusOptionsFor() above.
      await updateDoc(doc(db, "estimations", movingEstimation.id), {
  invoiced: true,
  estimationAmount: final,
});

      toast.success("Moved to Invoice Successfully");

      setShowMoveModal(false);
      setMovingEstimation(null);
      setFinalQuotation("");
      setAmountPaid("");
      setMoveError("");
      setPendingInvoiceData(null);
      await fetchEstimations();
    } catch (err) {
      console.error(err);
      toast.error("Failed to move estimation to invoice.");
    } finally {
      setMovingSaving(false);
    }
  };

  return (
    <div className="page-container estimation-page">
      <div className="page-header">
        <div>
          <h1>Estimation</h1>
          <p>Create and manage project estimations.</p>
        </div>

        <div className="estimation-summary-card">
          <span className="estimation-summary-label">Total Estimation Value</span>
          <span className="estimation-summary-value">{formatRupees(totalValue)}</span>
        </div>
      </div>

      <div className="estimation-toolbar">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by ID, name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="estimation-table-card">
        <AnimatePresence mode="wait">
          <motion.table
            key={loading ? "loading" : "loaded"}
            className="estimation-table"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <thead>
              <tr>
                <th>Client ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Address</th>
                <th>Project Type</th>
                <th>Status</th>
                <th>Estimation Amount</th>
                <th>Estimation</th>
                <th>Move To Invoice</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="empty-row">
                    Loading estimations...
                  </td>
                </tr>
              ) : filteredEstimations.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty-row">
                    No Estimations Found
                  </td>
                </tr>
              ) : (
                paginatedEstimations.map((e) => (
                  <tr key={e.id}>
                    <td>
                      {e.clientId || "-"}
                      {getProjectRef(clientsById, e.clientDocId) && (
                        <span style={PROJECT_REF_BADGE_STYLE}>
                          {getProjectRef(clientsById, e.clientDocId)}
                        </span>
                      )}
                    </td>
                    <td>{e.name || "-"}</td>
                    <td>{e.email || "-"}</td>
                    <td>{e.address || "-"}</td>
                    <td>{e.projectType || "-"}</td>
                    <td>
                      <span className="status-pill">{statusFor(e)}</span>
                    </td>
                    <td className="estimation-amount-cell">
                      {formatRupees(displayedEstimationAmount(e))}
                    </td>
                    <td>
                      <div className="action-icons">
                        <button
                          className="icon-btn budget-icon"
                          title="BOQ"
                          onClick={() => openBudgetPlanning(e)}
                        >
                          <FaWallet />
                        </button>
                        <button
                          className="icon-btn view-budget-icon"
                          title="View BOQ"
                          onClick={() => openViewBudget(e)}
                        >
                          <FaChartPie />
                        </button>
                      </div>
                    </td>
                    <td>
                      <button
                        className={`move-invoice-btn${
                          isMovable(e)
                            ? hasBudgetPlan(e)
                              ? " is-active"
                              : " needs-budget"
                            : ""
                        }`}
                        disabled={!isMovable(e)}
                        title={
                          e.invoiced
                            ? "Moved"
                            : !isMovable(e)
                            ? "Move To Invoice"
                            : hasBudgetPlan(e)
                            ? "Move To Invoice"
                            : "Please complete Budget Allocation before moving this project to Invoice."
                        }
                        onClick={() => openMoveToInvoice(e)}
                      >
                        <FaFileInvoiceDollar />
                        {e.invoiced ? "Moved" : "Move To Invoice"}
                      </button>
                    </td>
                    <td>
                      <div className="action-icons">
                        <button
                          className="icon-btn view-icon"
                          title="View Client"
                          onClick={() => openView(e)}
                        >
                          <FaEye />
                        </button>
                        <button
                          className="icon-btn edit-icon"
                          title="Edit Estimation"
                          onClick={() => openEdit(e)}
                        >
                          <FaEdit />
                        </button>
                        <button
  className="icon-btn delete-icon"
  title={
    e.invoiced
      ? "Already moved to Invoice — cannot delete or reject"
      : "Delete Estimation"
  }
  disabled={e.invoiced}
  onClick={() => deleteEstimation(e)}
>
  <FaTrash />
</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </motion.table>
        </AnimatePresence>
      </div>

      <Pagination {...estimationsPagination} />

      {/* View Modal (read-only) */}
      <AnimatePresence>
        {showViewModal && viewingEstimation && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="estimation-page-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Client Details</h2>

              <div className="view-grid">
                <div className="view-field">
                  <span className="view-label">Client ID</span>
                  <span className="view-value">
                    {viewingEstimation.clientId || "-"}
                    {getProjectRef(clientsById, viewingEstimation.clientDocId) && (
                      <span style={PROJECT_REF_BADGE_STYLE}>
                        {getProjectRef(clientsById, viewingEstimation.clientDocId)}
                      </span>
                    )}
                  </span>
                </div>
                <div className="view-field">
                  <span className="view-label">Name</span>
                  <span className="view-value">{viewingEstimation.name || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Phone</span>
                  <span className="view-value">{viewingEstimation.phone || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Email</span>
                  <span className="view-value">{viewingEstimation.email || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Address</span>
                  <span className="view-value">{viewingEstimation.address || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Project Type</span>
                  <span className="view-value">
                    {viewingEstimation.projectType || "-"}
                  </span>
                </div>
                <div className="view-field">
                  <span className="view-label">Status</span>
                  <span className="view-value">{viewingEstimation.status || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Estimation Amount</span>
                  <span className="view-value">
                    {formatRupees(viewingEstimation.estimationAmount)}
                  </span>
                </div>
                <div className="view-field">
                  <span className="view-label">Created Date</span>
                  <span className="view-value">
                    {formatDate(viewingEstimation.createdAt)}
                  </span>
                </div>
              </div>

              <div className="modal-buttons">
                <button className="cancel-btn" onClick={closeView}>
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && editingEstimation && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="estimation-page-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Edit Estimation</h2>

              <div className="form-grid">
                <input
                  type="text"
                  name="name"
                  placeholder="Client Name"
                  value={editForm.name}
                  onChange={handleEditChange}
                />

                <div className="form-field-wrap">
                  <input
                    type="text"
                    name="phone"
                    placeholder="Phone Number (10 digits)"
                    inputMode="numeric"
                    maxLength={10}
                    value={editForm.phone}
                    onChange={handleEditChange}
                  />
                  {editErrors.phone && (
                    <span className="field-error">{editErrors.phone}</span>
                  )}
                </div>

                <div className="form-field-wrap">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={editForm.email}
                    onChange={handleEditChange}
                  />
                  {editErrors.email && (
                    <span className="field-error">{editErrors.email}</span>
                  )}
                </div>

                <input
                  type="text"
                  name="address"
                  placeholder="Address"
                  value={editForm.address}
                  onChange={handleEditChange}
                />

                <select
                  name="projectType"
                  value={editForm.projectType}
                  onChange={handleEditChange}
                >
                  <option value="">Select Project Type</option>
                  {PROJECT_TYPE_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>

                {/* Estimation Amount is now read-only here — it is always
                    derived from Budget Planning (see saveBudgetPlan), so it
                    can no longer be typed in directly on this form. */}
                <input
                  type="text"
                  name="estimationAmount"
                  placeholder="Estimation Amount"
                  value={formatRupees(editForm.estimationAmount)}
                  disabled
                  title="Set automatically from Budget Planning"
                />
              </div>

              <div className="modal-buttons">
                <button className="cancel-btn" onClick={closeEdit}>
                  Cancel
                </button>

                <button className="save-btn" disabled={saving} onClick={saveEdit}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Move to Invoice Modal */}
      <AnimatePresence>
        {showMoveModal && movingEstimation && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="estimation-page-modal move-invoice-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Move to Invoice</h2>

              <div className="view-grid move-invoice-summary">
                <div className="view-field">
                  <span className="view-label">Client Name</span>
                  <span className="view-value">{movingEstimation.name || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Client ID</span>
                  <span className="view-value">{movingEstimation.clientId || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Original Estimation</span>
                  <span className="view-value">
                    {formatRupees(movingEstimation.estimationAmount)}
                  </span>
                </div>
              </div>

              <div className="move-invoice-form">
                <div className="form-field-wrap">
                  <label className="move-field-label">
                    Final Quotation (after negotiation)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 80,00,000"
                    value={finalQuotation}
                    onChange={handleFinalQuotationChange}
                    autoFocus
                  />
                </div>

                <div className="form-field-wrap">
                  <label className="move-field-label">Amount Paid</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 3,00,000"
                    value={amountPaid}
                    onChange={handleAmountPaidChange}
                  />
                  {moveError && <span className="field-error">{moveError}</span>}
                </div>

                <div className="balance-display">
                  <span className="view-label">Balance Amount</span>
                  <span className="balance-amount-value">
                    {formatRupees(moveBalanceAmount)}
                  </span>
                </div>
              </div>

              <div className="modal-buttons">
                <button
                  className="cancel-btn"
                  disabled={movingSaving}
                  onClick={closeMoveToInvoice}
                >
                  Cancel
                </button>

                <button
                  className="save-btn"
                  disabled={movingSaving}
                  onClick={handleMoveConfirmClick}
                >
                  {movingSaving ? "Processing..." : "Confirm & Move to Invoice"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rebalance Planned Budget? confirmation */}
      <AnimatePresence>
        {showRebalanceChoice && movingEstimation && pendingInvoiceData && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="estimation-page-modal rebalance-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Rebalance Planned Budget?</h2>
              <p className="rebalance-text">
                The negotiated final quotation is{" "}
                <strong>{formatRupees(pendingInvoiceData.final)}</strong>, different from
                the amount this client's material budget was planned against. Do you want
                to automatically rebalance every planned budget category
                proportionally so the total matches the new quotation?
              </p>

              <div className="modal-buttons">
                <button
                  className="cancel-btn"
                  disabled={movingSaving}
                  onClick={() => handleRebalanceChoice(false)}
                >
                  No, Edit Manually
                </button>

                <button
                  className="save-btn"
                  disabled={movingSaving}
                  onClick={() => handleRebalanceChoice(true)}
                >
                  Yes, Rebalance Automatically
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Budget Planning Modal — glass card, price/qty x qty rows with
          auto-computed totals, unlimited custom rows. Starts empty; the
          administrator adds rows manually via "+ Add Item". Saving this
          is what sets the Estimation/Quotation total — see
          saveBudgetPlan() above. */}
      <AnimatePresence>
        {showBudgetModal && budgetEstimation && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="budget-modal-v2"
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <div className="bmv2-header">
                <div>
                  <h2>
                    BOQ
                    {getProjectRef(clientsById, budgetEstimation.clientDocId) && (
                      <span style={PROJECT_REF_BADGE_STYLE}>
                        {getProjectRef(clientsById, budgetEstimation.clientDocId)}
                      </span>
                    )}
                  </h2>
                  <p className="bmv2-subtitle">
                    {budgetEstimation.name} — the total of every item below becomes this
                    client's Estimation amount.
                  </p>
                </div>
                <button
                  type="button"
                  className="bmv2-close"
                  disabled={budgetSaving}
                  onClick={closeBudgetModal}
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>

         <div className="bmv2-summary">
                <div className="bmv2-chip">
                  <span className="bmv2-chip-label">Estimation</span>
                  <span className="bmv2-chip-value">
                    {formatRupees(budgetTotalPlanned)}
                  </span>
                </div>
              </div>

              <div className="bmv2-rows">
                <div className="bmv2-row bmv2-row-head">
  <span>Items</span>
  <span>Quantity</span>
  <span>Price</span>
  <span>Total</span>
  <span>Notes</span>
  <span aria-hidden="true"></span>
</div>

                <div className="bmv2-rows-scroll" ref={budgetRowsScrollRef}>
                  <AnimatePresence initial={false}>
                    {budgetRows.map((row) => (
                      <motion.div
                        key={row.id}
                        className="bmv2-row"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                      >
                        <input
  type="text"
  className="bmv2-input"
  value={row.name}
  placeholder="Item name"
  ref={row.id === newlyAddedRowId ? newRowNameInputRef : undefined}
  onChange={(ev) =>
    handleBudgetRowChange(row.id, "name", ev.target.value)
  }
/>

<input
  type="text"
  inputMode="decimal"
  className="bmv2-input bmv2-input-qty"
  value={row.quantity}
  placeholder="0"
  onChange={(ev) =>
    handleBudgetRowChange(row.id, "quantity", ev.target.value)
  }
/>

<input
  type="text"
  inputMode="decimal"
  className="bmv2-input"
  value={row.pricePerQty}
  placeholder="₹0"
  onChange={(ev) =>
    handleBudgetRowChange(row.id, "pricePerQty", ev.target.value)
  }
/>

<span className="bmv2-total">
  {formatRupees(rowTotal(row))}
</span>

<input
  type="text"
  className="bmv2-input"
  value={row.notes}
  placeholder="Optional note"
  onChange={(ev) =>
    handleBudgetRowChange(row.id, "notes", ev.target.value)
  }
/>
                        <button
                          type="button"
                          className="bmv2-row-delete"
                          title="Remove item"
                          onClick={() => deleteBudgetRow(row.id)}
                        >
                          <FaTrash />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {budgetRows.length === 0 && (
                    <div className="bmv2-empty">
                      No items yet — use "Add Item" below to start planning.
                    </div>
                  )}
                </div>
              </div>

              <button type="button" className="bmv2-add-item" onClick={addBudgetRow}>
                <FaPlus /> Add Item
              </button>

              {budgetError && (
                <span className="field-error budget-error">{budgetError}</span>
              )}

              <div className="modal-buttons">
                <button className="cancel-btn" disabled={budgetSaving} onClick={closeBudgetModal}>
                  Cancel
                </button>
                <button className="save-btn" disabled={budgetSaving} onClick={saveBudgetPlan}>
                  {budgetSaving ? "Saving..." : "Save Budget Plan"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Budget Modal (read-only) */}
      <AnimatePresence>
        {showViewBudgetModal && viewBudgetData && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="budget-modal-v2 view-budget-v2"
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <div className="bmv2-header">
                <div>
                  <h2>BOQ Breakdown</h2>
                  <p className="bmv2-subtitle">Read-only planning snapshot.</p>
                </div>
                <button
                  type="button"
                  className="bmv2-close"
                  onClick={closeViewBudget}
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="bmv2-summary">
                <div className="bmv2-chip">
                  <span className="bmv2-chip-label">Estimation</span>
                  <span className="bmv2-chip-value">
                    {formatRupees(viewBudgetData.quotationAmount)}
                  </span>
                </div>
                <div className="bmv2-chip">
                  <span className="bmv2-chip-label">Planned Budget</span>
                  <span className="bmv2-chip-value">
                    {formatRupees(viewBudgetData.totalPlanned)}
                  </span>
                </div>
                <div
                  className={`bmv2-chip ${
                    (viewBudgetData.quotationAmount || 0) -
                      (viewBudgetData.totalPlanned || 0) <
                    0
                      ? "is-negative"
                      : "is-positive"
                  }`}
                >
                  <span className="bmv2-chip-label">Remaining Budget</span>
                  <span className="bmv2-chip-value">
                    {formatRupees(
                      (viewBudgetData.quotationAmount || 0) -
                        (viewBudgetData.totalPlanned || 0),
                    )}
                  </span>
                </div>
              </div>

              <div className="bmv2-rows read-only">
                <div className="bmv2-row bmv2-row-head view-row cols-5">
                  <span>Items</span>
                  <span>Price</span>
                  <span>Quantity</span>
                  <span>Total</span>
                  <span>Notes</span>
                </div>

                <div className="bmv2-rows-scroll">
                  {(viewBudgetData.items || []).length === 0 ? (
                    <div className="bmv2-empty">No items in this budget plan.</div>
                  ) : (
                    (viewBudgetData.items || []).map((item, idx) => {
                      const qty = item.quantity ?? 1;
                      const pricePerQty =
                        item.pricePerQty ??
                        (qty ? (Number(item.budget) || 0) / qty : Number(item.budget) || 0);
                      return (
                        <div className="bmv2-row view-row cols-5" key={idx}>
                          <span className="bmv2-static">{item.name}</span>
                          <span className="bmv2-static">{formatRupees(pricePerQty)}</span>
                          <span className="bmv2-static">{qty}</span>
                          <span className="bmv2-total">{formatRupees(item.budget)}</span>
                          <span className="bmv2-static">{item.notes || "-"}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="modal-buttons">
                <button
                  className="generate-pdf-btn"
                  disabled={budgetPdfGenerating}
                  onClick={generateBudgetPDF}
                  title="Download a Budget Allocation Report for this client"
                >
                  <FaFilePdf />
                  {budgetPdfGenerating ? "Generating..." : "Generate PDF"}
                </button>
                <button className="cancel-btn" onClick={closeViewBudget}>
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Delete / Reject choice — ONLY reachable when the estimation is
          not yet invoiced (deleteEstimation() blocks the invoiced case
          before this ever opens, and the delete icon is disabled too).
          Reuses the same estimation-page-modal shell as every other
          modal on this page. This fully replaces the old ConfirmDialog —
          there is no other delete confirmation path left on this page. */}
      <AnimatePresence>
        {pendingDeleteEstimation && !pendingDeleteEstimation.invoiced && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="estimation-page-modal reject-choice-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Remove this estimation?</h2>
              <p className="rebalance-text">
                "{pendingDeleteEstimation.name}" hasn't moved to Invoice yet. You can
                move them to Rejected — keeping their record — or permanently delete
                the estimation instead.
              </p>

              <div className="modal-buttons reject-choice-buttons">
                <button
                  className="cancel-btn"
                  disabled={deletingEstimation || movingEstToRejected}
                  onClick={() => setPendingDeleteEstimation(null)}
                >
                  Cancel
                </button>

                <button
                  className="save-btn reject-btn"
                  disabled={deletingEstimation || movingEstToRejected}
                  onClick={confirmMoveEstimationToRejected}
                >
                  {movingEstToRejected ? "Moving..." : "Move to Rejected"}
                </button>

                <button
                  className="save-btn danger-btn"
                  disabled={deletingEstimation || movingEstToRejected}
                  onClick={confirmDeleteEstimation}
                >
                  {deletingEstimation ? "Deleting..." : "Permanently Delete"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer toasts={toasts} onDismiss={toast.dismiss} />
    </div>
  );
}

export default Estimation1;